/**
 * /discover — Unified Discovery Page
 *
 * Calls all existing public endpoints in parallel (no new unified backend endpoint).
 * Client-side aggregation, search, and filtering across all content types.
 * Existing separate directory pages are untouched.
 */
import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQueries } from "@tanstack/react-query";
import {
  Search, PlayCircle, Loader2, MapPin, Star, DollarSign, Clock, Users, ShoppingBag,
  Sparkles, BookOpen, CreditCard, Calendar, User, ChevronRight, SlidersHorizontal, X,
} from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { money as fmtMoney } from "@/lib/format";

import RichContent from "@/components/RichContent";
// ─── Type definitions ────────────────────────────────────────────────────────
const TYPES = [
  { key: "all",       label: "All",         icon: Search },
  { key: "enhancer",  label: "Enhancers",   icon: Sparkles },
  { key: "center",    label: "Centers",     icon: MapPin },
  { key: "offering",  label: "Offerings",   icon: Star },
  { key: "event",     label: "Events",      icon: Calendar },
  { key: "course",    label: "Courses",     icon: BookOpen },
  { key: "membership",label: "Memberships", icon: CreditCard },
  { key: "product",   label: "Shop",        icon: ShoppingBag },
  { key: "member",    label: "Community",   icon: Users },
  { key: "media",     label: "Media",       icon: PlayCircle },
];

const SELLER_TYPE_LABELS = {
  musician: "Musician", crafter: "Crafter", herbalist: "Herbalist",
  vendor: "Vendor", enhancer: "Enhancer",
};

const money = (v) => v > 0 ? fmtMoney(v) : "Free";
const dur = (m) => m >= 60 ? `${Math.floor(m / 60)}h${m % 60 ? ` ${m % 60}m` : ""}` : `${m}m`;

// ─── Card components ─────────────────────────────────────────────────────────
function TypeBadge({ type }) {
  const map = {
    enhancer: "bg-[#144F75]/10 text-[#144F75]",
    center:   "bg-[#5C8A72]/10 text-[#5C8A72]",
    offering: "bg-[#D98E73]/15 text-[#B5794E]",
    event:    "bg-[#9E5C7F]/10 text-[#7D4968]",
    course:   "bg-[#2A6496]/10 text-[#2A6496]",
    membership:"bg-[#6A3D9A]/10 text-[#6A3D9A]",
    product:  "bg-[#4A5D6B]/10 text-[#4A5D6B]",
    member:   "bg-[#3E6B54]/10 text-[#3E6B54]",
    media:    "bg-[#D98E73]/15 text-[#9A5236]",
  };
  const t = TYPES.find((x) => x.key === type);
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${map[type] || "bg-[#E5E0D8] text-[#4A5D6B]"}`}>
      {t && <t.icon size={10} />}{t?.label || type}
    </span>
  );
}

function CardShell({ to, children, testId }) {
  return (
    <Link to={to} data-testid={testId}
      className="group block bg-white rounded-2xl border border-[#E5E0D8] hover:border-[#144F75]/30 hover:shadow-md transition-all duration-200 overflow-hidden">
      {children}
    </Link>
  );
}

function EnhancerCard({ item }) {
  return (
    <CardShell to={`/enhancers/${item.slug || item.id}`} testId={`discover-enhancer-${item.id}`}>
      <div className="h-28 bg-gradient-to-br from-[#144F75]/10 to-[#5C8A72]/10 relative overflow-hidden">
        {item.profile_image ? (
          <img src={item.profile_image} alt={item.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="w-14 h-14 rounded-full bg-[#144F75]/20 flex items-center justify-center text-[#144F75] font-serif text-2xl">
              {(item.name || "?")[0]}
            </div>
          </div>
        )}
      </div>
      <div className="p-3">
        <div className="flex items-start justify-between mb-1">
          <h3 className="font-semibold text-[#1A242B] text-sm leading-tight line-clamp-1">{item.name}</h3>
          <TypeBadge type="enhancer" />
        </div>
        {item.headline && <p className="text-xs text-[#4A5D6B] line-clamp-1 mb-1">{item.headline}</p>}
        {item.location && <p className="text-[11px] text-[#8FA89B] flex items-center gap-1"><MapPin size={10} />{item.location}</p>}
        {(item.modalities || []).length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {item.modalities.slice(0, 3).map((m) => <span key={m} className="text-[10px] bg-[#F1EEE8] px-1.5 py-0.5 rounded-full text-[#4A5D6B]">{m}</span>)}
          </div>
        )}
        <div className="mt-2 flex items-center justify-between">
          <span className="text-xs text-[#144F75] flex items-center gap-0.5">View profile <ChevronRight size={11} /></span>
        </div>
      </div>
    </CardShell>
  );
}

function CenterCard({ item }) {
  return (
    <CardShell to={`/locations/${item.slug || item.id}`} testId={`discover-center-${item.id}`}>
      <div className="h-28 bg-gradient-to-br from-[#5C8A72]/10 to-[#D98E73]/10 relative overflow-hidden">
        {item.image ? (
          <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <MapPin size={28} className="text-[#5C8A72]/40" />
          </div>
        )}
        {item.kind === "flagship" && (
          <span className="absolute top-2 left-2 text-[10px] font-bold bg-[#D98E73] text-white px-2 py-0.5 rounded-full">Flagship</span>
        )}
      </div>
      <div className="p-3">
        <div className="flex items-start justify-between mb-1">
          <h3 className="font-semibold text-[#1A242B] text-sm leading-tight line-clamp-1">{item.name}</h3>
          <TypeBadge type="center" />
        </div>
        {item.address && <p className="text-[11px] text-[#8FA89B] flex items-center gap-1"><MapPin size={10} />{item.address}</p>}
        {item.description && <RichContent preview text={item.description} className="text-xs text-[#4A5D6B] line-clamp-2 mt-1" />}
        <span className="mt-2 inline-flex items-center text-xs text-[#5C8A72] gap-0.5">View center <ChevronRight size={11} /></span>
      </div>
    </CardShell>
  );
}

function OfferingCard({ item }) {
  const mainCenter = item.centers?.[0];
  const mainEnhancer = item.enhancers?.[0];
  return (
    <CardShell to={`/offerings/${item.slug || item.id}`} testId={`discover-offering-${item.id}`}>
      <div className="h-28 bg-gradient-to-br from-[#D98E73]/10 to-[#144F75]/10 relative overflow-hidden">
        {item.image ? (
          <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center"><Star size={28} className="text-[#D98E73]/40" /></div>
        )}
        <div className="absolute bottom-2 right-2">
          <span className="text-xs font-bold bg-white/90 text-[#1A242B] px-2 py-0.5 rounded-full shadow-sm">{money(item.price)}</span>
        </div>
      </div>
      <div className="p-3">
        <div className="flex items-start justify-between mb-1">
          <h3 className="font-semibold text-[#1A242B] text-sm leading-tight line-clamp-1">{item.title}</h3>
          <TypeBadge type="offering" />
        </div>
        {mainEnhancer && <p className="text-xs text-[#4A5D6B]">with {mainEnhancer.name}</p>}
        <div className="flex items-center gap-2 mt-1 text-[11px] text-[#8FA89B]">
          {item.duration_min > 0 && <span className="flex items-center gap-0.5"><Clock size={10} />{dur(item.duration_min)}</span>}
          {mainCenter && <span className="flex items-center gap-0.5"><MapPin size={10} />{mainCenter.name}</span>}
          {item.virtual && !mainCenter && <span>Virtual</span>}
        </div>
        <span className="mt-2 inline-flex items-center text-xs text-[#D98E73] gap-0.5 font-medium">Book now <ChevronRight size={11} /></span>
      </div>
    </CardShell>
  );
}

function EventCard({ item }) {
  const dateStr = item.date ? new Date(item.date + "T00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "";
  return (
    <CardShell to={`/events/${item.slug || item.id}`} testId={`discover-event-${item.id}`}>
      <div className="h-28 bg-gradient-to-br from-[#9E5C7F]/10 to-[#144F75]/10 relative overflow-hidden">
        {item.image ? (
          <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center"><Calendar size={28} className="text-[#9E5C7F]/40" /></div>
        )}
        {dateStr && (
          <div className="absolute top-2 left-2 bg-white/90 rounded-lg px-2 py-1 text-center shadow-sm">
            <p className="text-[10px] text-[#9E5C7F] font-bold uppercase">{dateStr.split(" ")[0]}</p>
            <p className="text-sm font-bold text-[#1A242B] leading-tight">{dateStr.split(" ")[1]}</p>
          </div>
        )}
        {item.price > 0 && (
          <span className="absolute bottom-2 right-2 text-xs font-bold bg-white/90 text-[#1A242B] px-2 py-0.5 rounded-full shadow-sm">{money(item.price)}</span>
        )}
      </div>
      <div className="p-3">
        <div className="flex items-start justify-between mb-1">
          <h3 className="font-semibold text-[#1A242B] text-sm leading-tight line-clamp-1">{item.title}</h3>
          <TypeBadge type="event" />
        </div>
        {item.facilitator && <p className="text-xs text-[#4A5D6B]">with {item.facilitator}</p>}
        {item.center?.name && <p className="text-[11px] text-[#8FA89B] flex items-center gap-0.5 mt-0.5"><MapPin size={10} />{item.center.name}</p>}
        <span className="mt-2 inline-flex items-center text-xs text-[#9E5C7F] gap-0.5">View event <ChevronRight size={11} /></span>
      </div>
    </CardShell>
  );
}

function CourseCard({ item }) {
  return (
    <CardShell to={`/courses/${item.slug || item.id}`} testId={`discover-course-${item.id}`}>
      <div className="h-28 bg-gradient-to-br from-[#2A6496]/10 to-[#5C8A72]/10 relative overflow-hidden">
        {item.image ? (
          <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center"><BookOpen size={28} className="text-[#2A6496]/40" /></div>
        )}
        {item.price > 0 && (
          <span className="absolute bottom-2 right-2 text-xs font-bold bg-white/90 text-[#1A242B] px-2 py-0.5 rounded-full shadow-sm">{money(item.price)}</span>
        )}
      </div>
      <div className="p-3">
        <div className="flex items-start justify-between mb-1">
          <h3 className="font-semibold text-[#1A242B] text-sm leading-tight line-clamp-1">{item.title}</h3>
          <TypeBadge type="course" />
        </div>
        {item.short_description && <p className="text-xs text-[#4A5D6B] line-clamp-2">{item.short_description}</p>}
        <span className="mt-2 inline-flex items-center text-xs text-[#2A6496] gap-0.5">View course <ChevronRight size={11} /></span>
      </div>
    </CardShell>
  );
}

function MembershipCard({ item }) {
  return (
    <CardShell to="/memberships" testId={`discover-membership-${item.id}`}>
      <div className="h-28 bg-gradient-to-br from-[#6A3D9A]/10 to-[#D98E73]/10 relative overflow-hidden">
        <div className="w-full h-full flex items-center justify-center">
          <CreditCard size={32} className="text-[#6A3D9A]/30" />
        </div>
        {item.price > 0 && (
          <span className="absolute bottom-2 right-2 text-xs font-bold bg-white/90 text-[#1A242B] px-2 py-0.5 rounded-full shadow-sm">
            {money(item.price)}/{item.billing_period || "mo"}
          </span>
        )}
      </div>
      <div className="p-3">
        <div className="flex items-start justify-between mb-1">
          <h3 className="font-semibold text-[#1A242B] text-sm leading-tight line-clamp-1">{item.name || item.title}</h3>
          <TypeBadge type="membership" />
        </div>
        {item.tagline && <p className="text-xs text-[#4A5D6B] line-clamp-2">{item.tagline}</p>}
        <span className="mt-2 inline-flex items-center text-xs text-[#6A3D9A] gap-0.5 font-medium">Join <ChevronRight size={11} /></span>
      </div>
    </CardShell>
  );
}

function ProductCard({ item }) {
  const sellerLabel = SELLER_TYPE_LABELS[item.seller_type] || "Seller";
  return (
    <CardShell to={`/marketplace?product=${item.id}`} testId={`discover-product-${item.id}`}>
      <div className="h-28 bg-gradient-to-br from-[#4A5D6B]/10 to-[#D98E73]/10 relative overflow-hidden">
        {item.image ? (
          <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center"><ShoppingBag size={28} className="text-[#4A5D6B]/40" /></div>
        )}
        {item.price > 0 && (
          <span className="absolute bottom-2 right-2 text-xs font-bold bg-white/90 text-[#1A242B] px-2 py-0.5 rounded-full shadow-sm">{money(item.price)}</span>
        )}
        {item.seller_type && item.seller_type !== "enhancer" && (
          <span className="absolute top-2 left-2 text-[10px] font-bold bg-[#4A5D6B] text-white px-2 py-0.5 rounded-full">{sellerLabel}</span>
        )}
      </div>
      <div className="p-3">
        <div className="flex items-start justify-between mb-1">
          <h3 className="font-semibold text-[#1A242B] text-sm leading-tight line-clamp-1">{item.title}</h3>
          <TypeBadge type="product" />
        </div>
        {item.enhancer_name && <p className="text-xs text-[#4A5D6B]">by {item.enhancer_name}</p>}
        {item.short_description && <p className="text-xs text-[#8FA89B] line-clamp-1 mt-0.5">{item.short_description}</p>}
        <span className="mt-2 inline-flex items-center text-xs text-[#4A5D6B] gap-0.5">View item <ChevronRight size={11} /></span>
      </div>
    </CardShell>
  );
}

function MemberCard({ item }) {
  return (
    <Link to={`/community/members/${item.id}`} className="group block bg-white rounded-2xl border border-[#E5E0D8] hover:border-[#3E6B54]/30 hover:shadow-md transition-all duration-200 overflow-hidden" data-testid={`discover-member-${item.id}`}>
      <div className="p-3">
        <div className="flex items-start gap-2 mb-2">
          <div className="w-9 h-9 rounded-full bg-[#3E6B54]/15 flex items-center justify-center text-[#3E6B54] font-semibold text-sm shrink-0">
            {item.avatar ? <img src={item.avatar} alt={item.name} className="w-9 h-9 rounded-full object-cover" /> : (item.name || "?")[0]}
          </div>
          <div className="min-w-0">
            <div className="flex items-start justify-between gap-1">
              <h3 className="font-semibold text-[#1A242B] text-sm leading-tight">{item.name}</h3>
              <TypeBadge type="member" />
            </div>
            {item.headline && <p className="text-xs text-[#4A5D6B] line-clamp-1">{item.headline}</p>}
            {item.location && <p className="text-[11px] text-[#8FA89B] flex items-center gap-0.5"><MapPin size={9} />{item.location}</p>}
          </div>
        </div>
        {(item.interests || []).length > 0 && (
          <div className="flex flex-wrap gap-1">
            {item.interests.slice(0, 4).map((i) => <span key={i} className="text-[10px] bg-[#3E6B54]/8 text-[#3E6B54] px-1.5 py-0.5 rounded-full">{i}</span>)}
          </div>
        )}
        <span className="mt-2 inline-flex items-center text-xs text-[#3E6B54] gap-0.5" data-testid={`discover-member-cta-${item.id}`}>Connect <ChevronRight size={11} /></span>
      </div>
    </Link>
  );
}

const MEDIA_KIND = { live: "Live now", upcoming: "Live event", replay: "Replay", podcast: "Podcast" };
function MediaResultCard({ item }) {
  return (
    <Link to={item.url} data-testid={`discover-media-${item.url.split("/").filter(Boolean).slice(-2).join("-")}`} className="group block bg-white rounded-2xl border border-[#E5E0D8] hover:shadow-md transition-shadow overflow-hidden">
      <div className="h-28 bg-[#144F75]/10 relative overflow-hidden">
        {item.image ? <img src={item.image} alt={item.title} className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-[#144F75]/50"><PlayCircle size={28} /></div>}
        <span className="absolute top-2 left-2 text-[10px] font-bold bg-white/90 text-[#144F75] px-2 py-0.5 rounded-full">{MEDIA_KIND[item.kind] || "Media"}</span>
      </div>
      <div className="p-3">
        <div className="flex items-start justify-between gap-1 mb-1">
          <h3 className="font-semibold text-[#1A242B] text-sm leading-tight line-clamp-1">{item.title}</h3>
          <TypeBadge type="media" />
        </div>
        {item.host && <p className="text-xs text-[#4A5D6B]">with {item.host}</p>}
        <p className="text-[11px] text-[#8FA89B] mt-0.5">{{ public: "Public", members: "Members only", ticket: "Ticket holders" }[item.access] || "Public"}</p>
      </div>
    </Link>
  );
}

function ResultCard({ item }) {
  const C = {
    enhancer: EnhancerCard, center: CenterCard, offering: OfferingCard,
    event: EventCard, course: CourseCard, membership: MembershipCard,
    product: ProductCard, member: MemberCard, media: MediaResultCard,
  }[item._type];
  return C ? <C item={item} /> : null;
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function Discover() {
  const [q, setQ] = useState("");
  const [activeType, setActiveType] = useState("all");
  const [showFilters, setShowFilters] = useState(false);

  // Parallel queries to all existing endpoints (no new backend endpoint)
  const queries = useQueries({
    queries: [
      { queryKey: ["discover-enhancers"], queryFn: () => api.get("/enhancers").then((r) => r.data), refetchInterval: 60000 },
      { queryKey: ["discover-locations"], queryFn: () => api.get("/locations").then((r) => r.data), refetchInterval: 60000 },
      { queryKey: ["discover-offerings"], queryFn: () => api.get("/discovery/offerings").then((r) => r.data), refetchInterval: 60000 },
      { queryKey: ["discover-events"], queryFn: () => api.get("/discovery/events").then((r) => r.data), refetchInterval: 60000 },
      { queryKey: ["discover-courses"], queryFn: () => api.get("/courses").then((r) => r.data), refetchInterval: 60000 },
      { queryKey: ["discover-memberships"], queryFn: () => api.get("/memberships").then((r) => r.data), refetchInterval: 60000 },
      { queryKey: ["discover-products"], queryFn: () => api.get("/products").then((r) => r.data), refetchInterval: 60000 },
      { queryKey: ["discover-members"], queryFn: () => api.get("/members/public-discover").then((r) => r.data).catch(() => []), refetchInterval: 60000 },
      { queryKey: ["discover-media"], queryFn: () => api.get("/media-hub/search").then((r) => r.data).catch(() => []), refetchInterval: 60000 },
    ],
  });

  const isLoading = queries.some((q) => q.isLoading);

  const [d0, d1, d2, d3, d4, d5, d6, d7, d8] = queries.map((x) => x.data);

  const allItems = useMemo(() => {
    const items = [];
    (d0 || []).forEach((e) => items.push({ ...e, _type: "enhancer" }));
    (d1 || []).forEach((c) => items.push({ ...c, _type: "center" }));
    ((d2?.offerings) || []).forEach((o) => items.push({ ...o, _type: "offering" }));
    ((d3?.events) || []).forEach((e) => items.push({ ...e, _type: "event" }));
    (d4 || []).forEach((c) => items.push({ ...c, _type: "course" }));
    (d5 || []).forEach((m) => items.push({ ...m, _type: "membership" }));
    (d6 || []).forEach((p) => items.push({ ...p, _type: "product" }));
    (d7 || []).forEach((m) => items.push({ ...m, _type: "member" }));
    (d8 || []).forEach((m) => items.push({ ...m, id: m.url, _type: "media" }));
    return items;
  }, [d0, d1, d2, d3, d4, d5, d6, d7, d8]); // eslint-disable-line

  const filtered = useMemo(() => {
    let result = allItems;
    if (activeType !== "all") result = result.filter((i) => i._type === activeType);
    if (q.trim()) {
      const lq = q.toLowerCase();
      result = result.filter((i) =>
        (i.title || i.name || "").toLowerCase().includes(lq) ||
        (i.headline || i.short_description || "").toLowerCase().includes(lq) ||
        (i.location || "").toLowerCase().includes(lq) ||
        (i.enhancer_name || i.facilitator || i.host || "").toLowerCase().includes(lq) ||
        (i.tags || []).some((x) => String(x).toLowerCase().includes(lq)) ||
        (i.interests || []).some((x) => x.toLowerCase().includes(lq)) ||
        (i.modalities || []).some((x) => x.toLowerCase().includes(lq))
      );
    }
    return result;
  }, [allItems, q, activeType]);

  // Counts per type for badges
  const counts = useMemo(() => {
    const c = { all: allItems.length };
    allItems.forEach((i) => { c[i._type] = (c[i._type] || 0) + 1; });
    return c;
  }, [allItems]);

  return (
    <div className="min-h-screen bg-[#FDFCF9]" data-testid="discover-page">
      <Seo title="Discover | Enhancing Your World" description="Find enhancers, centers, offerings, events, courses, marketplace products, and community members — all in one place." />

      {/* Hero */}
      <div className="bg-gradient-to-b from-[#0E3A54] to-[#144F75] text-white py-16 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <p className="eyw-label text-white/60 mb-2">Unified Discovery</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-white mb-3">Find Your Path</h1>
          <p className="text-white/70 text-base mb-8">Enhancers, centers, services, events, courses, products, and community — search everything from one place.</p>

          {/* Search bar */}
          <div className="relative max-w-2xl mx-auto">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8FA89B]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search offerings, enhancers, events, products…"
              data-testid="discover-search"
              className="w-full pl-11 pr-12 py-4 rounded-2xl text-[#1A242B] bg-white shadow-lg text-base focus:outline-none focus:ring-2 focus:ring-white/40"
            />
            {q && (
              <button onClick={() => setQ("")} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#8FA89B] hover:text-[#4A5D6B]">
                <X size={16} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Type filter chips */}
      <div className="sticky top-0 z-20 bg-white border-b border-[#E5E0D8] shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-2 overflow-x-auto hide-scrollbar">
          {TYPES.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveType(key)}
              data-testid={`discover-type-${key}`}
              className={`shrink-0 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium border transition-all ${
                activeType === key
                  ? "bg-[#144F75] text-white border-[#144F75]"
                  : "bg-white text-[#4A5D6B] border-[#E5E0D8] hover:border-[#144F75]/40 hover:text-[#144F75]"
              }`}
            >
              <Icon size={13} />{label}
              {counts[key] > 0 && (
                <span className={`text-[10px] font-bold ml-0.5 ${activeType === key ? "text-white/80" : "text-[#8FA89B]"}`}>
                  {counts[key]}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Results */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Result count */}
        <div className="flex items-center justify-between mb-5">
          <p className="text-sm text-[#4A5D6B]">
            {isLoading ? (
              <span className="flex items-center gap-1.5"><Loader2 size={13} className="animate-spin" /> Loading…</span>
            ) : (
              <span><strong className="text-[#1A242B]">{filtered.length}</strong> result{filtered.length !== 1 ? "s" : ""}
                {q ? ` for "${q}"` : ""}
                {activeType !== "all" ? ` in ${TYPES.find((t) => t.key === activeType)?.label}` : ""}
              </span>
            )}
          </p>
          <p className="text-[11px] text-[#C5BFB6] flex items-center gap-1">
            <span className="h-1.5 w-1.5 rounded-full bg-[#5C8A72] inline-block" />Live · updates every 60s
          </p>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-52 bg-[#F1EEE8] rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <Search size={32} className="text-[#C5BFB6] mx-auto mb-3" />
            <p className="font-serif text-xl text-[#1A242B] mb-1">Nothing found</p>
            <p className="text-sm text-[#8FA89B]">Try a different search term or browse all types.</p>
            <button onClick={() => { setQ(""); setActiveType("all"); }} className="mt-4 eyw-btn !py-2 text-sm">Clear filters</button>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {filtered.map((item, idx) => (
              <ResultCard key={`${item._type}-${item.id}-${idx}`} item={item} />
            ))}
          </div>
        )}

        {/* Footer links to existing pages */}
        {!q && activeType === "all" && !isLoading && (
          <div className="mt-12 pt-8 border-t border-[#E5E0D8]">
            <p className="text-xs text-center text-[#8FA89B] mb-4">Browse by category</p>
            <div className="flex flex-wrap justify-center gap-3 text-sm">
              {[
                { to: "/wellness", label: "All Offerings" },
                { to: "/events", label: "Events & Retreats" },
                { to: "/enhancers", label: "Enhancers" },
                { to: "/locations", label: "Centers" },
                { to: "/courses", label: "Courses" },
                { to: "/marketplace", label: "Shop" },
                { to: "/memberships", label: "Memberships" },
                { to: "/community", label: "Community" },
              ].map(({ to, label }) => (
                <Link key={to} to={to} className="text-[#144F75] hover:underline">{label}</Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
